import { MoreVertRounded } from "@mui/icons-material";
import { Chip, IconButton, Menu, MenuItem, Stack, styled, SxProps, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import {
  InlineTextField,
  isWhitespace,
  ItemDrawerTitle,
  LinkBehavior,
  TaskItemContextLabel,
  TaskItemDrawerContent,
  TaskItemIcons,
  TaskItemStatusPicker,
  useDrawer,
} from "src/components";
import { DEFAULT_TASK_ITEM_STATUS, Links } from "src/constants";
import { useBreakpoints } from "src/hooks";
import { TaskItemDto, useDeleteTaskItem, useEditTaskItem, UserDto } from "src/http";
import { useDestructiveConfirm } from "src/utilities";

const StyledStack = styled(Stack)`
  cursor: pointer;

  .editMenuButton {
    opacity: 0;
  }

  &:hover {
    .editMenuButton {
      opacity: 1;
    }
  }
`;

interface IProps {
  taskItem: TaskItemDto;
  userCanEdit: boolean;
  showTeamChip?: boolean;
  onOwnershipChanged?: (taskItemGuid: string, taskItemDescription: string, removedOwners: UserDto[]) => void;
  sx?: SxProps;
}

const TaskItem = (props: IProps) => {
  const { showDrawer, closeDrawer } = useDrawer();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { mutate: editTaskItem } = useEditTaskItem();
  const { mutate: deleteTaskItem } = useDeleteTaskItem();
  const theme = useTheme();
  const destructiveConfirm = useDestructiveConfirm();
  const ref = useRef<HTMLInputElement>(null);
  const { isMdUp } = useBreakpoints();

  function handleTaskItemClicked() {
    showDrawer({
      title: <ItemDrawerTitle link={Links.TaskItemDetail(props.taskItem.guid)} />,
      content: (
        <TaskItemDrawerContent taskItemGuid={props.taskItem.guid} onOwnershipChanged={props.onOwnershipChanged} />
      ),
    });
  }

  function handleTaskItemBlurred(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (props.userCanEdit) {
      editTaskItem({
        taskItemGuid: props.taskItem.guid,
        description: e.target.value,
      });
    }
  }

  async function handleDeleteClicked(e: React.MouseEvent) {
    setMenuAnchor(null);
    e.stopPropagation();

    const { confirmed } = await destructiveConfirm({
      title: "Delete Task",
      content: "Are you sure you want to delete this task?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    // Close the drawer in case it's open.
    closeDrawer();

    deleteTaskItem({
      taskItemGuid: props.taskItem.guid,
    });
  }

  const shouldDisplayPropertyIcons =
    props.taskItem.owners.length > 0 ||
    (props.taskItem.note != null && !isWhitespace(props.taskItem.note)) ||
    props.taskItem.dueDate != null ||
    props.taskItem.commentCount > 0;

  return (
    <StyledStack
      direction="row"
      sx={{ alignItems: "center", py: 0, ...props.sx, width: "100%", minWidth: 0 }}
      onClick={handleTaskItemClicked}
    >
      <Stack spacing={0.2} sx={{ py: 0.25, overflow: "hidden", width: "100%" }}>
        {props.showTeamChip && props.taskItem.team && (
          <Stack direction="row" spacing={0.5} sx={{ pl: 1.75, pt: 0.5 }}>
            <Chip
              color="primary"
              component={LinkBehavior}
              label={props.taskItem.team.name}
              href={Links.TeamImplementation(props.taskItem.team.slug)}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              clickable
            />
            <Chip label="Tasks" />
          </Stack>
        )}

        <Stack spacing={-0.25} sx={{ overflow: "hidden", width: "100%" }}>
          <TaskItemContextLabel taskItem={props.taskItem} condensed sx={{ pl: 1.75 }} />

          <Stack
            direction="row"
            sx={{
              alignItems: "start",
              overflow: "hidden",
              width: "100%",
            }}
          >
            <TaskItemStatusPicker
              variant="button"
              taskItemGuid={props.taskItem.guid}
              status={props.taskItem.currentStatus ?? DEFAULT_TASK_ITEM_STATUS}
              teamSlug={props.taskItem.team?.slug}
              ownerUserGuids={props.taskItem.owners.map((x) => x.user.userId)}
              disabled={!props.userCanEdit}
              sx={{ position: "relative", top: 4 }}
            />

            <InlineTextField
              ref={ref}
              value={props.taskItem.description}
              onClick={(e) => props.userCanEdit && e.stopPropagation()}
              onBlur={handleTaskItemBlurred}
              placeholder="Enter item..."
              isEditable={props.userCanEdit}
              maxLength={512}
            />
          </Stack>
        </Stack>

        {shouldDisplayPropertyIcons && <TaskItemIcons taskItem={props.taskItem} sx={{ pl: 1.75, pb: 0.25 }} />}
      </Stack>

      {isMdUp && props.userCanEdit && (
        <IconButton
          className="editMenuButton"
          size="small"
          onClick={(e) => {
            setMenuAnchor(e.currentTarget);
            e.stopPropagation();
          }}
        >
          <MoreVertRounded />
          <Menu
            open={Boolean(menuAnchor)}
            onClose={(e: React.PointerEvent) => {
              setMenuAnchor(null);
              e.stopPropagation();
            }}
            anchorEl={menuAnchor}
          >
            <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
              Delete
            </MenuItem>
          </Menu>
        </IconButton>
      )}
    </StyledStack>
  );
};

export { TaskItem };
